/*
EXAMPLE USAGE — options (value-based, dispatches 'change' event)

<fancy-select label="Select a league">
  <option value="nfl" data-image="{{ 'nfl.png' | asset_url }}" selected>NFL</option>
  <option value="mlb" data-image="{{ 'mlb.png' | asset_url }}">MLB</option>
  <option value="nba" data-image="{{ 'nba.png' | asset_url }}">NBA</option>
</fancy-select>

EXAMPLE USAGE — links (navigates on select)

<fancy-select label="Shop by league">
  <a href="/collections/nfl" data-image="{{ 'nfl.png' | asset_url }}" data-selected>NFL</a>
  <a href="/collections/mlb" data-image="{{ 'mlb.png' | asset_url }}">MLB</a>
  <a href="/collections/nba" data-image="{{ 'nba.png' | asset_url }}">NBA</a>
</fancy-select>

ATTRIBUTES
  label          — accessible label for the listbox (default: "Select an option")
  data-variant   — "centered" for large centered look (56px, 40×40 image); default is compact (40px, 24×24, left-aligned)
  bg-color       — Tailwind bg class for trigger + dropdown (default: bg-primary-light)
  border-color   — Tailwind border class for trigger + dropdown (default: border-primary-dark/20)
  option-hover-bg — CSS color value for option hover/selected bg (default: color-mix dark 8%)
  no-redirect    — links mode only: dispatch 'change' (detail.value = href) instead of navigating
  trigger-class  — extra classes appended to the trigger button
  option-class   — extra classes appended to each option <li>
*/

if (!customElements.get('fancy-select')) {
  if (!document.getElementById('fancy-select-styles')) {
    const style = document.createElement('style');
    style.id = 'fancy-select-styles';
    style.textContent = `
      fancy-select { position: relative; display: block; }
      fancy-select:not(:defined) { display: block; height: 2.5rem; background: #e5e5e5; }
      fancy-select:not(:defined)[data-variant="centered"] { height: 3.5rem; }
      fancy-select:not(:defined) * { display: none; }
      fancy-select[disabled] { cursor: not-allowed; }
      .fs-error-state .fs-trigger { border-color: var(--color-error) }
      .fs-error-state [role="listbox"] { border-color: var(--color-error) }
      .fs-option:hover, .fs-option:focus { background: var(--fs-hover-bg, color-mix(in srgb, var(--color-primary-dark) 8%, transparent)); outline: none; }
      .fs-option[aria-selected="true"], .fs-option[data-value=""] { display: none; }
      .fs-option[data-current="true"] { background: var(--fs-hover-bg, color-mix(in srgb, var(--color-primary-dark) 5%, transparent)); }
      fancy-select [role="listbox"] { position: absolute; top: 100%; bottom: auto; left: 0; right: 0; z-index: 9999; }
      fancy-select.fs-opens-up [role="listbox"] { top: auto; bottom: 100%; }
      .fs-chevron { transition: transform 150ms ease; }
      .fs-trigger[aria-expanded="true"] .fs-chevron { transform: rotate(180deg); }
      .fs-trigger[data-opens-up][aria-expanded="true"] { border-top-color: transparent; }
      .fs-trigger:hover .fs-chevron { transform: scale(1.3); }
      .fs-trigger[aria-expanded="true"]:hover .fs-chevron { transform: rotate(180deg) scale(1.3); }
      .fs-opens-up { border-top-width: 1px !important; border-bottom-width: 0 !important; }
      fancy-select.fs-opens-up [role="listbox"] { border-top-width: 1px !important; border-bottom-width: 0 !important; }
    `;
    document.head.appendChild(style);
  }

  customElements.define('fancy-select', class FancySelect extends HTMLElement {
    connectedCallback() {
      // Save intersection-loaders before build() replaces innerHTML
      const loaders = Array.from(this.querySelectorAll('intersection-loader'));

      const anchors = Array.from(this.querySelectorAll('a'));
      this.isLinks  = anchors.length > 0;

      const currentPath = window.location.pathname;
      this.opts = this.isLinks
        ? anchors.map(a => ({
            value:    a.getAttribute('href'),
            text:     a.textContent.trim(),
            image:    a.dataset.image ?? null,
            selected: a.hasAttribute('data-selected') || new URL(a.href, window.location.origin).pathname === currentPath,
            index:    a.dataset.index != null ? Number(a.dataset.index) : null,
          }))
        : Array.from(this.querySelectorAll('option')).map(o => ({
            value:    o.value,
            text:     o.text ?? o.textContent.trim(),
            image:    o.dataset.image ?? null,
            selected: o.hasAttribute('selected'),
            soldOut:  o.dataset.soldOut === 'true',
            index:    o.dataset.index != null ? Number(o.dataset.index) : null,
          }));

      this.val         = this.opts.find(o => o.selected)?.value ?? this.opts[0]?.value;
      this.variant     = this.dataset.variant ?? 'default';
      this.bgColor     = this.getAttribute('bg-color') ?? 'bg-primary-light';
      this.borderColor = this.getAttribute('border-color') ?? 'border-primary-dark/20';
      const hoverBg    = this.getAttribute('option-hover-bg');
      if (hoverBg) this.style.setProperty('--fs-hover-bg', hoverBg);
      this.focusedIdx = 0;
      this.open       = false;
      this.uid        = `fs-${Math.random().toString(36).slice(2)}`;

      this.opts.forEach(o => { if (o.image) { new Image().src = o.image; } });

      this.build();
      this.bind();
      if (this._pendingValue !== undefined) { this.select(this._pendingValue); this._pendingValue = undefined; }

      if (loaders.length) {
        // Re-attach loaders in a hidden slot so they can run their fetches
        this._loaderSlot = document.createElement('div');
        this._loaderSlot.hidden = true;
        loaders.forEach(l => this._loaderSlot.appendChild(l));
        this.appendChild(this._loaderSlot);

        this._onIntersectionLoaded = () => {
          Array.from(this._loaderSlot.querySelectorAll('option')).forEach(o => {
            console.log(o)
            const opt = {
              value:   o.value,
              text:    o.text ?? o.textContent.trim(),
              image:   o.dataset.image ?? null,
              selected: o.hasAttribute('selected'),
              soldOut: o.dataset.soldOut === 'true',
              index: o.dataset.index
            };
            this.opts.push(opt);
            this._appendOption(opt);
            if (opt.image) new Image().src = opt.image;
            o.remove();
          });
        };
        document.addEventListener('intersection:loaded', this._onIntersectionLoaded);
      }
    }

    disconnectedCallback() {
      if (this._onIntersectionLoaded) {
        document.removeEventListener('intersection:loaded', this._onIntersectionLoaded);
      }
    }

    get value() { return this.val; }
    set value(v) { if (this.opts) this.select(v); else this._pendingValue = v; }

    // Public API for consumers that fetch additional options asynchronously
    // (e.g. a batched logo lookup) and want to append them once resolved.
    addOptions(opts) {
      opts.forEach(opt => {
        this.opts.push(opt);
        this._appendOption(opt);
        if (opt.image) new Image().src = opt.image;
      });
    }

    // Toggle disabled after build() (e.g. once async data changes whether the select applies).
    setDisabled(flag) {
      this.toggleAttribute('disabled', flag);
      if (!this.trigger) return;
      if (flag) this.close();
      this.trigger.toggleAttribute('disabled', flag);
      if (flag) this.trigger.setAttribute('aria-disabled', 'true');
      else this.trigger.removeAttribute('aria-disabled');
      this.trigger.classList.toggle('cursor-not-allowed', flag);
      this.trigger.classList.toggle('pointer-events-none', flag);
      this.trigger.querySelector('.fs-chevron')?.classList.toggle('opacity-20', flag);
    }

    _sortedOpts() {
      return this.opts.slice().sort((a, b) => {
        if (a.index == null && b.index == null) return 0;
        if (a.index == null) return 1;
        if (b.index == null) return -1;
        return a.index - b.index;
      });
    }

    build() {
      const { uid, val, bgColor, borderColor } = this;
      const opts         = this._sortedOpts();
      const sel          = opts.find(o => o.value === val) ?? opts[0];
      const triggerExtra = this.getAttribute('trigger-class') ?? '';
      const optionExtra  = this.getAttribute('option-class') ?? '';

      const isCentered  = this.variant === 'centered';
      const triggerH    = isCentered ? 'h-14'         : 'h-10';
      const gap         = isCentered ? 'gap-4'        : 'gap-3';
      const textAlign   = isCentered ? 'justify-center' : 'justify-start';
      const optPadding  = isCentered ? 'py-4'         : 'py-3';
      const isDisabled  = this.hasAttribute('disabled');

      this.innerHTML = `
        <button
          type="button"
          role="combobox"
          aria-label="${this.getAttribute('label') ?? 'Select an option'}"
          aria-haspopup="listbox"
          aria-expanded="false"
          aria-controls="${uid}-lb"
          ${isDisabled ? 'disabled aria-disabled="true"' : ''}
          class="fs-trigger w-full flex items-center ${gap} px-5 ${triggerH} border ${borderColor} ${bgColor} text-body font-body cursor-pointer ${triggerExtra}${isDisabled ? ' cursor-not-allowed pointer-events-none' : ''}"
        >
          ${sel.image ? `<span class="fs-trigger-img flex items-center shrink-0">${this.triggerImg(sel)}</span>` : ''}
          <span class="fs-selected flex-1 flex items-center ${textAlign} min-w-0">
            <span class="truncate  capitalize text-xs${sel.soldOut ? ' line-through opacity-40' : ''}">${sel.text}</span>
          </span>
          ${this.hasAttribute('no-chevron') ? '' : `<span class="fs-chevron shrink-0${isDisabled ? ' opacity-20' : ''}" aria-hidden="true">
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </span>`}
        </button>
        <ul
          id="${uid}-lb"
          role="listbox"
          aria-label="${this.getAttribute('label') ?? 'Select an option'}"
          tabindex="-1"
          class="${bgColor} border ${borderColor} border-t-0 list-none m-0 p-0 max-h-64 overflow-y-auto hidden"
        >
          ${opts.map((o, i) => {
            const isCurrent = o.value === val;
            const ariaSelected = this.isLinks ? 'false' : String(o.value !== '' && isCurrent);
            return `
            <li
              id="${uid}-opt-${i}"
              role="option"
              data-value="${o.value}"
              ${o.index != null ? `data-sort-index="${o.index}"` : ''}
              aria-selected="${ariaSelected}"
              ${o.value === '' ? 'aria-disabled="true"' : ''}
              ${this.isLinks && isCurrent ? 'data-current="true"' : ''}
              tabindex="-1"
              class="fs-option flex items-center ${gap} px-5 ${optPadding} cursor-pointer text-body font-body transition-colors${isCurrent ? ' font-medium' : ''} ${optionExtra}"
            >${this.optHTML(o)}</li>
          `}).join('')}
        </ul>`;

      this.trigger = this.querySelector('.fs-trigger');
      this.listbox = this.querySelector('[role="listbox"]');
      this.items   = Array.from(this.querySelectorAll('[role="option"]'));
    }

    _appendOption(opt) {
      const gap        = this.variant === 'centered' ? 'gap-4' : 'gap-3';
      const optPadding = this.variant === 'centered' ? 'py-4'  : 'py-3';
      const optionExtra = this.getAttribute('option-class') ?? '';
      const idx        = this.opts.length - 1;
      const isCurrent  = opt.value === this.val;

      const li = document.createElement('li');
      li.id    = `${this.uid}-opt-${idx}`;
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '-1');
      li.setAttribute('aria-selected', this.isLinks ? 'false' : String(opt.value !== '' && isCurrent));
      if (this.isLinks && isCurrent) li.dataset.current = 'true';
      li.dataset.value = opt.value;
      if (opt.index != null) li.dataset.sortIndex = opt.index;
      li.className = `fs-option flex items-center ${gap} px-5 ${optPadding} cursor-pointer text-body font-body transition-colors${isCurrent ? ' font-medium' : ''} ${optionExtra}`;
      li.innerHTML = this.optHTML(opt);

      if (opt.index != null) {
        const insertBefore = this.items.find(item =>
          item.dataset.sortIndex != null && Number(item.dataset.sortIndex) > opt.index
        );
        if (insertBefore) {
          this.listbox.insertBefore(li, insertBefore);
          this.items.splice(this.items.indexOf(insertBefore), 0, li);
        } else {
          this.listbox.appendChild(li);
          this.items.push(li);
        }
      } else {
        this.listbox.appendChild(li);
        this.items.push(li);
      }
    }

    triggerImg(opt) {
      const cls = this.variant === 'centered' ? 'w-10 h-10' : 'w-6 h-6';
      return opt.image
        ? `<img src="${opt.image}" alt="" aria-hidden="true" class="${cls} object-contain shrink-0">`
        : '';
    }

    optHTML(opt) {
      const cls = this.variant === 'centered' ? 'w-10 h-10' : 'w-6 h-6';
      const img = opt.image
        ? `<img src="${opt.image}" alt="" aria-hidden="true" class="${cls} object-contain shrink-0">`
        : '';
      const oosCls = opt.soldOut ? ' line-through opacity-40' : '';
      return `${img}<span class="truncate  capitalize text-xs${oosCls}">${opt.text}</span>`;
    }

    bind() {
      this.trigger.addEventListener('click', () => this.toggle());
      this.trigger.addEventListener('keydown', e => this.triggerKey(e));
      this.addEventListener('click', e => {
        const item = e.target.closest('[role="option"]');
        if (item) this.select(item.dataset.value);
      });
      this.addEventListener('keydown', e => {
        if (e.target === this.trigger) return; // handled by triggerKey
        if (this.open) this.listboxKey(e);
      });
      document.addEventListener('click', e => { if (!this.contains(e.target)) this.close(); }, { passive: true });
    }

    toggle() { this.open ? this.close() : this.openMenu(); }

    openMenu() {
      this.open = true;
      this.trigger.setAttribute('aria-expanded', 'true');

      const rect = this.trigger.getBoundingClientRect();
      const opensUp = (window.innerHeight - rect.bottom) < 200 && rect.top > (window.innerHeight - rect.bottom);
      this.classList.toggle('fs-opens-up', opensUp);
      this.trigger.toggleAttribute('data-opens-up', opensUp);

      this.listbox.classList.remove('hidden');
      const firstVisible = this.items.findIndex(i => this._isSelectable(i));
      this.focusedIdx = Math.max(0, firstVisible);
      this.focusItem(this.focusedIdx);
    }

    close() {
      this.open = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      this.trigger.removeAttribute('data-opens-up');
      this.classList.remove('fs-opens-up');
      this.listbox.classList.add('hidden');
    }

    select(value) {
      const opt = this.opts.find(o => o.value === value);
      if (!opt) return;

      if (this.isLinks) {
        if (this.hasAttribute('no-redirect')) {
          this.close();
          this.dispatchEvent(new CustomEvent('change', { detail: { value }, bubbles: true }));
          return;
        }
        window.location = value;
        return;
      }

      this.val = value;

      this.items.forEach(item => {
        const sel = item.dataset.value === value;
        item.setAttribute('aria-selected', sel);
        item.classList.toggle('font-medium', sel);
      });

      const imgWrap = this.trigger.querySelector('.fs-trigger-img');
      if (imgWrap) imgWrap.innerHTML = this.triggerImg(opt);
      this.trigger.querySelector('.fs-selected').innerHTML =
        `<span class="truncate  capitalize text-xs${opt.soldOut ? ' line-through opacity-40' : ''}">${opt.text}</span>`;

      this.close();
      if (!this.closest('swiper-slide')) this.trigger.focus();
      this.dispatchEvent(new CustomEvent('change', { detail: { value }, bubbles: true }));
    }

    _isSelectable(item) {
      return item.getAttribute('aria-selected') !== 'true' && item.dataset.value !== '';
    }

    nextVisible(from, dir) {
      let i = from + dir;
      while (i >= 0 && i < this.items.length) {
        if (this._isSelectable(this.items[i])) return i;
        i += dir;
      }
      return from;
    }

    focusItem(idx) {
      this.focusedIdx = idx;
      const item = this.items[idx];
      item.focus();
      this.trigger.setAttribute('aria-activedescendant', item.id);
    }

    triggerKey(e) {
      switch (e.key) {
        case 'ArrowDown': case 'Enter': case ' ':
          e.preventDefault(); this.openMenu(); break;
        case 'ArrowUp':
          e.preventDefault(); this.openMenu(); break;
        case 'Tab':
          if (this.open) { e.preventDefault(); this.close(); } break;
        case 'Escape':
          this.close(); break;
      }
    }

    listboxKey(e) {
      const last = this.items.length - 1;
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); this.focusItem(this.nextVisible(this.focusedIdx,  1)); break;
        case 'ArrowUp':   e.preventDefault(); this.focusItem(this.nextVisible(this.focusedIdx, -1)); break;
        case 'Home':      e.preventDefault(); this.focusItem(0);    break;
        case 'End':       e.preventDefault(); this.focusItem(last); break;
        case 'Enter': case ' ':
          e.preventDefault(); this.select(this.items[this.focusedIdx].dataset.value); break;
        case 'Escape':
          this.close(); this.trigger.focus(); break;
        case 'Tab':
          this.close(); break;
      }
    }
  });
}
